import React from 'react';
import {
  Button,
  useUpdate,
  useNotify,
  useRefresh,
  useRecordContext,
  Confirm
} from 'react-admin';
import LockOpenIcon from '@mui/icons-material/LockOpen';

/**
 * Button to unlock a user account that was locked due to failed login attempts.
 * This button is shown only when the user account is locked.
 * 
 * @returns {JSX.Element|null}
 */
const UnlockUserButton = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const [open, setOpen] = React.useState(false);
  
  const [update, { isLoading }] = useUpdate(
    'users',
    { 
      id: record?.id, 
      data: { 
        account_locked: false,
        failed_login_attempts: 0,
        locked_at: null
      },
      previousData: record
    },
    {
      onSuccess: () => {
        notify('User account unlocked successfully', { type: 'success' });
        refresh();
        setOpen(false);
      },
      onError: (error) => {
        notify(`Error unlocking user: ${error.message}`, { type: 'error' });
        setOpen(false);
      }
    }
  );

  // Don't show button if record doesn't exist or account is not locked
  if (!record || !record.account_locked) {
    return null;
  }

  const handleClick = () => setOpen(true);
  const handleDialogClose = () => setOpen(false);
  const handleConfirm = () => update();

  return (
    <>
      <Button
        label="Unlock Account"
        onClick={handleClick}
        disabled={isLoading}
      >
        <LockOpenIcon />
      </Button>
      <Confirm
        isOpen={open}
        loading={isLoading}
        title="Unlock User Account"
        content={`Are you sure you want to unlock the account for ${record.email || record.user_name}?`}
        onConfirm={handleConfirm}
        onClose={handleDialogClose}
      />
    </>
  );
};

export default UnlockUserButton;

